import classNames from 'classnames';

import React, { useCallback, useContext, useState } from 'react';

import { useForm } from 'react-hook-form';

import { useLocation, useNavigate } from 'react-router-dom';

import { useQueryClient } from 'react-query';

import { produce } from 'immer';

import { SessionContext, SessionContextType } from '@contexts/session-context';
import {
  UserPreferenceContext,
  UserPreferenceContextType,
} from '@contexts/user-preference-context';

import {
  useExternalEvents,
  useTranslation,
  Button,
  Icon,
  toastify,
  handleFieldUpdate,
} from '@testsigma/atoms';

import { SalesforceConnectionsService } from '@modules/salesforce/services';
import StrollEventService from '@services/strollr-event.service';

import { ApplicationVersion } from '../../models/application-version.model';
import { Application } from '../../models/application.model';
import { Project } from '../../models/project.model';
import ProjectAutoComplete from './project-auto-complete';
import VersionAutoComplete from './version-auto-complete';

import { AccessLevel } from '@enums/access-level.enum';
import ApplicationAutoComplete from './application-auto-complete';

interface ProjectPreferenceType {
  selectedProject: Project;
  selectedApplication: Application;
  selectedVersion: ApplicationVersion;
}

interface ListItemProps {
  project?: Project;
  application?: Application;
  version?: ApplicationVersion;
  updateProjectCallBack?: (data: Project) => void;
  updateApplicationCallBack?: (data: Application) => void;
  updateVersionCallBack?: (data: ApplicationVersion) => void;
  toggleDropdown?: (flag: boolean) => void;
  setIsSidebarhovered?: (flag: boolean) => void;
  toggleProjectSettings?: (flag: boolean) => void;
}

const SwitcherContainer: React.FC<ListItemProps> = ({
  project,
  application,
  version,
  updateProjectCallBack,
  updateApplicationCallBack,
  updateVersionCallBack,
  toggleDropdown,
  setIsSidebarhovered,
  toggleProjectSettings,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const userPreferenceContext = useContext(
    UserPreferenceContext,
  ) as UserPreferenceContextType;

  const publishExternalEvents = useExternalEvents('refresh-session', []);
  const sessionContext = useContext(SessionContext) as SessionContextType;
  const [isFetching, setIsFetching] = useState({
    applications: false,
    versions: false,
  });
  const isApplicationsOrVersionsFetching =
    isFetching.applications || isFetching.versions;

  const defaultValues = { project, application, version };

  const {
    handleSubmit,
    watch,
    formState: { isDirty },
    setValue,
  } = useForm({
    defaultValues,
  });

  const selectedProject = watch('project');
  const selectedApplication = watch('application');
  const selectedVersion = watch('version');
  const createProjectPath = 'projects/new';

  const navigateUrl = useCallback(
    (url: string, onlyRedirect: boolean = false) => {
      toggleDropdown(false);
      setIsSidebarhovered(false);
      navigate(url, { state: { from: pathname } });

      if (onlyRedirect) return;

      const isProjectChanged =
        selectedProject?.id !== defaultValues.project?.id;
      const isApplicationChanged =
        selectedApplication?.id !== defaultValues.application?.id;
      const isVersionChanged =
        selectedVersion?.id !== defaultValues?.version?.id;

      const changesType = isProjectChanged
        ? 'project'
        : isApplicationChanged
        ? 'application'
        : 'version';

      {
        (isProjectChanged || isApplicationChanged || isVersionChanged) &&
          toastify(t(`project.switch.${changesType}.success.info`), {
            type: 'success',
            position: 'top-center',
            hideProgressBar: true,
          });
      }
    },
    [defaultValues, selectedApplication, selectedProject, selectedVersion],
  );

  const redirectTo = (url: string) => {
    navigateUrl(url);
    publishExternalEvents('refresh-session', 'refresh');
    sessionContext.refreshSession();
  };

  const updateUserPreference = () => {
    const urlSplit = pathname.split('/').splice(1);
    const projectPreference =
      userPreferenceContext?.userPreference?.preference?.projectPreference;
    const userPreferenceApplicationIsSalesforce =
      projectPreference?.selectedApplication?.isSalesforce;
    const projectPreferenceVersionId = projectPreference.versionId;
    const selectedVersionId = selectedVersion.id;

    if (pathname === '/dashboard') {
      return redirectTo(pathname);
    }

    if (
      projectPreference?.selectedVersion?.application?.isRest &&
      (pathname.includes('uploads') || pathname.includes('elements'))
    ) {
      // Rest API project doesn't have uploads and elements page
      return redirectTo('/dashboard');
    }

    if (userPreferenceApplicationIsSalesforce) {
      return SalesforceConnectionsService.findAll(
        `?query=,applicationVersionId:${projectPreferenceVersionId},connectionType@METADATA`,
      ).then((res) => {
        if (res?.content?.length) {
          return redirectTo(`/td/${projectPreferenceVersionId}/cases/filters`);
        }

        return redirectTo(
          `/salesforce/${projectPreferenceVersionId}/connection`,
        );
      });
    }

    if (!userPreferenceApplicationIsSalesforce && !urlSplit.includes('td')) {
      return redirectTo(`/td/${projectPreferenceVersionId}/cases/filters`);
    }

    if (
      urlSplit[0].includes('td') &&
      urlSplit[1].includes('test_case_results')
    ) {
      return redirectTo(
        `/${urlSplit[0]}/${projectPreferenceVersionId}/results`,
      );
    }

    if (urlSplit[0].includes('td') && urlSplit[1].includes('dry_run')) {
      return redirectTo(
        `/${urlSplit[0]}/${projectPreferenceVersionId}/cases/filters`,
      );
    }

    if (new RegExp(/td\/((?!(?=(\d+))).*)\/(\d+)/g).test(pathname)) {
      return redirectTo(`/${urlSplit[0]}/${selectedVersionId}/${urlSplit[1]}`);
    }

    if (urlSplit[0].includes('td') || urlSplit[0].includes('project')) {
      return redirectTo(`/${urlSplit[0]}/${selectedVersionId}/${urlSplit[2]}`);
    }

    return redirectTo(pathname);
  };

  const updateProjectDetails = () => {
    updateProjectCallBack(selectedProject);
    updateApplicationCallBack(selectedApplication);
    updateVersionCallBack(selectedVersion);
  };
  const queryClient = useQueryClient();
  const onSubmit = () => {
    // when we switch project, we need to clear the query cache
    queryClient.clear();
    localStorage.removeItem('store:filterData');
    localStorage.removeItem('lastVisitedTestCaseId');
    updateProjectDetails();

    const userPreference = userPreferenceContext?.userPreference;

    const { id: versionId } = selectedVersion;
    const { id: projectId } = selectedProject;
    const {
      id: applicationId,
      applicationType,
      isMobileNative,
    } = selectedApplication;

    userPreference.preference.projectPreference = produce(
      userPreference.preference.projectPreference,
      (draft) => {
        draft.selectedVersion = selectedVersion;
        draft.selectedProject = selectedProject;
        draft.selectedApplication = selectedApplication;
        draft.versionId = versionId;
        draft.projectId = projectId;
        draft.applicationId = applicationId;
      },
    );
    userPreferenceContext.setOrUpdatePreference(
      userPreference,
      updateUserPreference,
    );

    toggleDropdown(false);
    setIsSidebarhovered(false);

    window.tsGlobal = window.tsGlobal || {};
    window.tsGlobal.applicationType = applicationType;

    StrollEventService.trackUserProperty({
      applicationType: isMobileNative ? 'Mobile' : 'Web',
    });
  };

  const updateProject = (data: Project) => {
    handleFieldUpdate({ setValue, fieldName: 'project', value: data });

    updateProjectCallBack(data);
  };

  const updateApplication = (data: Application) => {
    handleFieldUpdate({ setValue, fieldName: 'application', value: data });

    updateApplicationCallBack(data);
  };

  const updateVersion = (data: ApplicationVersion) => {
    handleFieldUpdate({ setValue, fieldName: 'version', value: data });

    updateVersionCallBack(data);
  };

  const isWriteAccess = (name: string) => {
    const entityAccessList = sessionContext.session.user.privileges.filter(
      (access) =>
        access.entity.name !== 'plugins' &&
        access.entity.name !== 'test_group_report',
    );
    return (
      entityAccessList.filter((access) => {
        return (
          access.getAccessLevel(name) &&
          (AccessLevel[access.accessLevel] === AccessLevel.WRITE ||
            AccessLevel[access.accessLevel] === AccessLevel.FULL_ACCESS)
        );
      }).length > 0
    );
  };

  const userHasWriteAccess = isWriteAccess('project');

  const ProjectSettingsButton = () => (
    <button
      onClick={() => toggleProjectSettings(true)}
      className='flex items-center absolute right-5 top-5 text-[13px] font-medium hover:underline cursor-pointer z-10'
    >
      <Icon className='text-lg pr-sm text-unique-800' name='settings' />
      {t('project_switcher.project_setting')}
    </button>
  );

  return (
    <form
      className='w-[300px] flex flex-wrap max-h-[385px] items-baseline transition ease-in-out duration-200 border-neutral-200 p-[18px] rounded-lg shadow-slate-300 relative'
      onSubmit={handleSubmit(onSubmit)}
    >
      <ProjectSettingsButton />

      <ProjectAutoComplete
        selectedItem={selectedProject}
        updateSelected={updateProject}
        dropdownClassName='!left-[235px]'
      />
      <ApplicationAutoComplete
        project={selectedProject}
        selectedItem={selectedApplication}
        updateSelected={updateApplication}
        dropdownClassName='!left-[235px]'
        setIsFetchingApplications={(flag: boolean) =>
          setIsFetching({ ...isFetching, applications: flag })
        }
        dropdownClassName='!left-[235px]'
      />
      <VersionAutoComplete
        application={selectedApplication}
        selectedItem={selectedVersion}
        updateSelected={updateVersion}
        setIsFetchingVersions={(flag: boolean) =>
          setIsFetching({ ...isFetching, versions: flag })
        }
        dropdownClassName='!left-[235px]'
      />
      <div
        className={classNames(
          'flex items-center mb-1 w-full',
          userHasWriteAccess ? 'justify-between' : 'justify-end',
        )}
      >
        {userHasWriteAccess && (
          <div
            onClick={() => navigateUrl(createProjectPath, true)}
            className='flex items-center text-[13px] font-medium hover:underline cursor-pointer z-10'
          >
            <Icon className='text-lg pr-sm text-unique-800' name='add-circle' />
            {t('project_switcher.create_new')}
          </div>
        )}
        <Button
          type='primary'
          isDisabled={!isDirty || isApplicationsOrVersionsFetching}
        >
          {isApplicationsOrVersionsFetching && (
            <Icon name='loader' className='text-base animate-spin mr-2' />
          )}
          {isApplicationsOrVersionsFetching
            ? t('common.loading_apps')
            : t('project_switcher.go')}
        </Button>
      </div>
    </form>
  );
};

export default SwitcherContainer;
